import { ArrowUpward, Download, Refresh } from '@mui/icons-material';
import { Box, Button, Chip, IconButton, Pagination, Tooltip, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { addQueue } from 'app/actions/notification';
import MapFilterReportDialog from 'app/pages/Map/MapFilterReportDialog';
import UserReportDialog from 'app/pages/User/UserReportDialog';
import { reportTypeLoad } from 'app/store/report';
import { getTaskParams, setTaskSort, TASKS_PAGE_SIZE, taskDownload, taskLoad } from 'app/store/task';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FixedSizeList } from 'react-window';
const columnsConfig = [
    { key: 'id', label: 'ID', width: 80, minWidth: 60, sortable: true },
    { key: 'type', label: 'Тип отчёта', width: 200, minWidth: 120, sortable: true },
    { key: 'period', label: 'Период', width: 220, minWidth: 150, sortable: false },
    { key: 'status', label: 'Статус', width: 140, minWidth: 100, sortable: true },
    { key: 'created_at', label: 'Создана', width: 160, minWidth: 120, sortable: true },
    { key: 'actions', label: '', width: 120, minWidth: 100, sortable: false },
];
const reportDialogs = {
    speed_limit: MapFilterReportDialog,
    speed_limit_advanced: MapFilterReportDialog,
    parking_time: MapFilterReportDialog,
    signal_off: MapFilterReportDialog,
    user_log: UserReportDialog,
    flight: MapFilterReportDialog,
    drains: MapFilterReportDialog,
};
const typeLabels = {
    speed_limit: 'Превышения скорости',
    parking_time: 'Стоянки',
    signal_off: 'Потери связи',
    user_log: 'Действия пользователей',
    flight: 'Рейсы',
    drains: 'Сливы',
    sep_events: 'События СЭП',
};
const statusColors = {
    pending: 'info',
    processing: 'warning',
    completed: 'success',
    failed: 'error',
};
const statusLabels = {
    pending: 'Ожидание',
    processing: 'Выполняется',
    completed: 'Завершено',
    failed: 'Ошибка',
};
const TasksPage = () => {
    const dispatch = useDispatch();
    const tasks = useSelector((state) => { var _a; return (Array.isArray((_a = state.task) === null || _a === void 0 ? void 0 : _a.items) ? state.task.items : []); });
    const sortField = useSelector((state) => { var _a; return ((_a = state.task) === null || _a === void 0 ? void 0 : _a.sortField) || 'created_at'; });
    const sortOrder = useSelector((state) => { var _a; return ((_a = state.task) === null || _a === void 0 ? void 0 : _a.sortOrder) || 'DESC'; });
    const page = useSelector((state) => { var _a; return ((_a = state.task) === null || _a === void 0 ? void 0 : _a.page) || 1; });
    const total = useSelector((state) => { var _a; return ((_a = state.task) === null || _a === void 0 ? void 0 : _a.total) || 0; });
    const totalPages = useSelector((state) => { var _a; return ((_a = state.task) === null || _a === void 0 ? void 0 : _a.totalPages) || 1; });
    const loading = useSelector((state) => { var _a; return Boolean((_a = state.task) === null || _a === void 0 ? void 0 : _a.loading); });
    const [dialog, setDialog] = useState(null);
    const [columnsWidth, setColumnsWidth] = useState(() => columnsConfig.reduce((widths, column) => {
        widths[column.key] = column.width;
        return widths;
    }, {}));
    const [resizing, setResizing] = useState(null);
    const listRef = React.useRef(null);
    const firstColumnKey = columnsConfig[0].key;
    const lastColumnKey = columnsConfig[columnsConfig.length - 1].key;
    const getColumnSx = useCallback((colKey, { header = false } = {}) => (Object.assign({ width: columnsWidth[colKey], minWidth: columnsWidth[colKey], flexShrink: 0, display: 'flex', alignItems: 'center', pl: colKey === firstColumnKey ? 3 : 1, pr: colKey === lastColumnKey ? 3 : 1, overflow: 'hidden', boxSizing: 'border-box' }, (header ? { position: 'relative', userSelect: 'none' } : {}))), [columnsWidth, firstColumnKey, lastColumnKey]);
    const handleMouseDownResize = useCallback((key, event) => {
        event.preventDefault();
        setResizing(key);
    }, []);
    useEffect(() => {
        const handleMouseMove = (event) => {
            if (!resizing)
                return;
            const column = columnsConfig.find((item) => item.key === resizing);
            if (!column)
                return;
            const tableHeaderRow = document.querySelector(`[data-column-header="${resizing}"]`);
            if (!tableHeaderRow)
                return;
            const rect = tableHeaderRow.getBoundingClientRect();
            const newWidth = Math.max(column.minWidth, Math.min(event.clientX - rect.left, 500));
            setColumnsWidth((prev) => (Object.assign(Object.assign({}, prev), { [resizing]: newWidth })));
        };
        const handleMouseUp = () => {
            setResizing(null);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [resizing]);
    useEffect(() => {
        dispatch(taskLoad());
        dispatch(reportTypeLoad());
    }, [dispatch]);
    const sortedTasks = useMemo(() => {
        const sorted = [...tasks];
        sorted.sort((left, right) => {
            let leftValue;
            let rightValue;
            switch (sortField) {
                case 'id':
                    leftValue = left.id;
                    rightValue = right.id;
                    break;
                case 'type':
                    leftValue = typeLabels[left.type] || left.type;
                    rightValue = typeLabels[right.type] || right.type;
                    break;
                case 'status':
                    leftValue = left.status;
                    rightValue = right.status;
                    break;
                case 'created_at':
                default:
                    leftValue = moment(left.created_at).valueOf();
                    rightValue = moment(right.created_at).valueOf();
                    break;
            }
            if (typeof leftValue === 'string' && typeof rightValue === 'string') {
                return sortOrder === 'ASC' ? leftValue.localeCompare(rightValue) : rightValue.localeCompare(leftValue);
            }
            return sortOrder === 'ASC' ? Number(leftValue) - Number(rightValue) : Number(rightValue) - Number(leftValue);
        });
        return sorted;
    }, [tasks, sortField, sortOrder]);
    const handleSort = (key) => {
        if (sortField === key) {
            dispatch(setTaskSort(key, sortOrder === 'ASC' ? 'DESC' : 'ASC'));
        }
        else {
            dispatch(setTaskSort(key, 'ASC'));
        }
    };
    const handleDownload = (task) => dispatch(taskDownload(task));
    const handleRetry = () => {
        dispatch(addQueue({
            text: 'Повторная генерация отчёта пока недоступна.',
            color: 'warning',
            timeout: 5000,
        }));
    };
    const handleDialogClose = () => {
        setDialog(null);
        dispatch(taskLoad(page));
    };
    const handlePageChange = (_event, nextPage) => {
        if (nextPage !== page) {
            dispatch(taskLoad(nextPage));
        }
    };
    const renderHeader = () => (React.createElement(Box, { sx: {
            display: 'flex',
            alignItems: 'center',
            height: 40,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            position: 'sticky',
            top: 0,
            zIndex: 2,
        } }, columnsConfig.map((column) => (React.createElement(Box, { key: column.key, "data-column-header": column.key, sx: getColumnSx(column.key, { header: true }) },
        column.sortable ? (React.createElement(Box, { onClick: () => handleSort(column.key), sx: { flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer' } },
            React.createElement(Typography, { variant: "caption", color: "text.secondary", noWrap: true },
                column.label,
                sortField === column.key && (React.createElement(ArrowUpward, { sx: {
                        fontSize: 14,
                        ml: 0.5,
                        verticalAlign: 'middle',
                        transform: sortOrder === 'DESC' ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.2s',
                    } }))))) : (React.createElement(Typography, { variant: "caption", color: "text.secondary", noWrap: true }, column.label)),
        React.createElement(Box, { onMouseDown: (event) => handleMouseDownResize(column.key, event), sx: {
                position: 'absolute',
                right: -2,
                top: 0,
                bottom: 0,
                width: 4,
                cursor: 'col-resize',
                '&:hover': { bgcolor: 'primary.light' },
            } }))))));
    const renderRow = ({ index, style }) => {
        const task = sortedTasks[index];
        if (!task) {
            return null;
        }
        return (React.createElement(Box, { style: style, sx: {
                display: 'flex',
                alignItems: 'center',
                boxSizing: 'border-box',
                borderBottom: 1,
                borderColor: 'divider',
                '&:hover': { bgcolor: alpha('#000', 0.04) },
            } }, columnsConfig.map((column) => {
            const commonSx = getColumnSx(column.key);
            switch (column.key) {
                case 'id':
                    return (React.createElement(Box, { key: column.key, sx: commonSx },
                        React.createElement(Typography, { variant: "body2", noWrap: true }, task.id)));
                case 'type':
                    return (React.createElement(Box, { key: column.key, sx: commonSx },
                        React.createElement(Typography, { variant: "body2", noWrap: true }, typeLabels[task.type] || task.type)));
                case 'period': {
                    const { from, to } = getTaskParams(task);
                    const period = from && to ? `${moment(from).format('DD.MM.YYYY')} – ${moment(to).format('DD.MM.YYYY')}` : '—';
                    return (React.createElement(Box, { key: column.key, sx: commonSx },
                        React.createElement(Typography, { variant: "body2", noWrap: true }, period)));
                }
                case 'status':
                    return (React.createElement(Box, { key: column.key, sx: commonSx },
                        React.createElement(Chip, { label: statusLabels[task.status] || task.status, color: statusColors[task.status] || 'default', size: "small" })));
                case 'created_at':
                    return (React.createElement(Box, { key: column.key, sx: commonSx },
                        React.createElement(Typography, { variant: "body2", noWrap: true }, moment(task.created_at).format('DD.MM.YYYY HH:mm'))));
                case 'actions':
                    return (React.createElement(Box, { key: column.key, sx: commonSx },
                        task.status === 'completed' && (React.createElement(Tooltip, { title: "\u0421\u043A\u0430\u0447\u0430\u0442\u044C" },
                            React.createElement(IconButton, { size: "small", onClick: (event) => {
                                    event.stopPropagation();
                                    handleDownload(task);
                                } },
                                React.createElement(Download, { fontSize: "small" })))),
                        task.status === 'failed' && (React.createElement(Button, { size: "small", variant: "text", onClick: handleRetry }, "\u041F\u043E\u0432\u0442\u043E\u0440\u0438\u0442\u044C"))));
                default:
                    return React.createElement(Box, { key: column.key, sx: commonSx });
            }
        })));
    };
    const tableWidth = useMemo(() => columnsConfig.reduce((sum, column) => sum + columnsWidth[column.key], 0), [columnsWidth]);
    const listHeight = Math.min(600, Math.max(sortedTasks.length, 1) * 48);
    const rangeFrom = sortedTasks.length === 0 ? 0 : (page - 1) * TASKS_PAGE_SIZE + 1;
    const rangeTo = sortedTasks.length === 0 ? 0 : rangeFrom + sortedTasks.length - 1;
    return (React.createElement(Box, { sx: { p: 3, height: '100%' } },
        React.createElement(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 } },
            React.createElement(Typography, { variant: "h5" }, "\u0417\u0430\u0434\u0430\u0447\u0438 \u043D\u0430 \u0444\u043E\u0440\u043C\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u0435 \u043E\u0442\u0447\u0451\u0442\u043E\u0432"),
            React.createElement(Box, { sx: { display: 'flex', gap: 1, flexWrap: 'wrap' } },
                React.createElement(Button, { startIcon: React.createElement(Refresh, null), variant: "outlined", onClick: () => dispatch(taskLoad(page)), disabled: loading }, "\u041E\u0431\u043D\u043E\u0432\u0438\u0442\u044C"))),
        React.createElement(Box, { sx: {
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'auto',
                bgcolor: 'background.paper',
            } },
            React.createElement(Box, { sx: { minWidth: tableWidth } },
                renderHeader(),
                !loading && sortedTasks.length === 0 ? (React.createElement(Box, { sx: { height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' } },
                    React.createElement(Typography, { variant: "body2", color: "text.secondary" }, "\u041D\u0435\u0442 \u0437\u0430\u0434\u0430\u0447"))) : (React.createElement(FixedSizeList, { ref: listRef, height: listHeight, itemCount: sortedTasks.length, itemSize: 48, width: tableWidth, overscanCount: 10 }, renderRow)))),
        React.createElement(Box, { sx: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, gap: 2, flexWrap: 'wrap' } },
            React.createElement(Typography, { variant: "body2", color: "text.secondary" }, total === 0 ? 'Нет задач' : `${rangeFrom}–${rangeTo} из ${total}`),
            React.createElement(Pagination, { color: "primary", count: totalPages, disabled: loading || totalPages <= 1, page: Math.min(page, totalPages), onChange: handlePageChange })),
        dialog && React.createElement(CreateReportDialog, { method: dialog.method, open: !!dialog, onClose: handleDialogClose })));
};
const CreateReportDialog = ({ method, open, onClose }) => {
    const DialogComponent = reportDialogs[method];
    if (!DialogComponent)
        return null;
    return React.createElement(DialogComponent, { open: open, onClose: onClose });
};
export default TasksPage;
